import Cookies from "js-cookie";
import { useUserStoreHook } from "@/store/modules/user";
import { storageLocal, isString, isIncludeAllChildren } from "@pureadmin/utils";
import { isTauri } from "@tauri-apps/api/core";
// import projectSetting from "@/setting/cacheEnum";
// const { permissionCacheType } = projectSetting;
// const isLocal = permissionCacheType === 'LOCAL';
export interface DataInfo<T> {
  /** token */
  accessToken: string;
  /** `accessToken`的过期时间（时间戳） */
  expires: T;
  /** 用于调用刷新accessToken的接口时所需的token */
  refreshToken: string;
  /** 头像 */
  avatar?: string;
  /** 用户名 */
  username?: string;
  /** 昵称 */
  nickname?: string;
  /** 当前登录用户的角色 */
  roles?: Array<string>;
  /** 当前登录用户的按钮级别权限 */
  permissions?: Array<string>;
}

export const userKey = "user-info";
export const TokenKey = "authorized-token";
/**
 * 通过`multiple-tabs`是否在`cookie`中，判断用户是否已经登录系统，
 * 从而支持多标签页打开已经登录的系统后无需再登录。
 * 浏览器完全关闭后`multiple-tabs`将自动从`cookie`中销毁，
 * 再次打开浏览器需要重新登录系统
 * */
export const multipleTabsKey = "multiple-tabs";

/** 获取token字符串（只返回accessToken） */
export function getToken(): string | null {
  const cookieToken = Cookies.get(TokenKey);

  if (cookieToken) {
    try {
      const obj = JSON.parse(cookieToken);
      return obj.accessToken || null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  const localToken = isTauri()
    ? (localStorage.getItem(TokenKey) as string)
    : (storageLocal().getItem(userKey) as any);

  if (isTauri() && localToken) {
    const localTokenObj = JSON.parse(localToken);
    return localTokenObj.accessToken;
  } else {
    return localToken?.accessToken ?? null;
  }
}

/** 获取完整token对象(包含accessToken、refreshToken、expires等) */
export function getFullToken(): DataInfo<number> | null {
  const cookieToken = Cookies.get(TokenKey);
  if (cookieToken) {
    try {
      return JSON.parse(cookieToken);
    } catch {
      return null;
    }
  }
  const localToken = isTauri()
    ? (localStorage.getItem(TokenKey) as string)
    : (storageLocal().getItem(userKey) as DataInfo<number>);
  if (isTauri() && localToken) {
    const localTokenObj = JSON.parse(localToken as string);
    return localTokenObj;
  } else {
    return (localToken as DataInfo<number>) ?? null;
  }
}

/**
 * @description 设置`token`以及一些必要信息并采用无感刷新`token`方案
 * 无感刷新：后端返回`accessToken`（访问接口使用的`token`）、`refreshToken`（用于调用刷新`accessToken`的接口时所需的`token`，`refreshToken`的过期时间（比如30天）应大于`accessToken`的过期时间（比如2小时））、`expires`（`accessToken`的过期时间）
 * 将`accessToken`、`expires`、`refreshToken`这三条信息放在key值为authorized-token的cookie里（过期自动销毁）
 * 将`avatar`、`username`、`nickname`、`roles`、`permissions`、`refreshToken`、`expires`这七条信息放在key值为`user-info`的localStorage里（利用`multipleTabsKey`当浏览器完全关闭后自动销毁）
 */
export function setToken(data: any) {
  let expires = 0;
  const { accessToken, refreshToken } = data;
  // 兼容后端返回expires为字符串或Date
  if (typeof data.expires === "string") {
    // 支持时间戳字符串或ISO格式
    const ts = Date.parse(data.expires);
    expires = isNaN(ts) ? Number(data.expires) : ts;
  } else if (typeof data.expires === "number") {
    expires = data.expires;
  } else if (data.expires instanceof Date) {
    expires = data.expires.getTime();
  }
  const cookieString = JSON.stringify({ accessToken, expires, refreshToken });

  // expires > 0
  //   ? Cookies.set(TokenKey, cookieString, {
  //       expires: (expires - Date.now()) / 86400000
  //     })
  //   : Cookies.set(TokenKey, cookieString);
  if (isTauri()) {
    localStorage.setItem(TokenKey, cookieString);
    localStorage.setItem(multipleTabsKey, "true");
  } else {
    Cookies.set(TokenKey, cookieString, {
      expires: 30
    });

    Cookies.set(multipleTabsKey, "true", {
      expires: 30
    });
  }

  function setUserKey({ avatar, username, nickname, roles, permissions }) {
    useUserStoreHook().SET_AVATAR(avatar);
    useUserStoreHook().SET_USERNAME(username);
    useUserStoreHook().SET_NICKNAME(nickname);
    useUserStoreHook().SET_ROLES(roles);
    useUserStoreHook().SET_PERMS(permissions);
    localStorage.setItem(
      userKey,
      JSON.stringify({
        refreshToken,
        expires,
        avatar,
        username,
        nickname,
        roles,
        permissions
      })
    );
  }

  if (data.username && data.roles) {
    const { username, roles } = data;
    setUserKey({
      avatar: data?.avatar ?? "",
      username,
      nickname: data?.nickname ?? "",
      roles,
      permissions: data?.permissions ?? []
    });
  } else {
    let avatar;
    let username;
    let nickname;
    let roles;
    let permissions;
    if (isTauri()) {
      const userInfo =
        JSON.parse(localStorage.getItem(userKey) as string) || {};
      avatar = userInfo.avatar ?? "";
      username = userInfo.username ?? "";
      nickname = userInfo.nickname ?? "";
      roles = userInfo.roles ?? [];
      permissions = userInfo.permissions ?? [];
    } else {
      avatar = storageLocal().getItem<DataInfo<number>>(userKey)?.avatar ?? "";
      username =
        storageLocal().getItem<DataInfo<number>>(userKey)?.username ?? "";
      nickname =
        storageLocal().getItem<DataInfo<number>>(userKey)?.nickname ?? "";
      roles = storageLocal().getItem<DataInfo<number>>(userKey)?.roles ?? [];
      permissions =
        storageLocal().getItem<DataInfo<number>>(userKey)?.permissions ?? [];
    }
    setUserKey({
      avatar,
      username,
      nickname,
      roles,
      permissions
    });
  }
}

/** 删除`token`以及key值为`user-info`的localStorage信息 */
export function removeToken() {
  if (isTauri()) {
    localStorage.removeItem(TokenKey);
    localStorage.removeItem(multipleTabsKey);
  } else {
    Cookies.remove(TokenKey);
    Cookies.remove(multipleTabsKey);
  }
  if (isTauri()) {
    localStorage.removeItem(userKey);
  } else {
    storageLocal().removeItem(userKey);
  }
}

/** 格式化token（jwt格式） */
export const formatToken = (token: string): string => {
  return "Bearer " + token;
};

/** 是否有按钮级别的权限（根据登录接口返回的`permissions`字段进行判断）*/
export const hasPerms = (value: string | Array<string>): boolean => {
  if (!value) return false;
  const allPerms = "*:*:*";
  const { permissions } = useUserStoreHook();
  if (!permissions) return false;
  if (permissions.length === 1 && permissions[0] === allPerms) return true;
  const isAuths = isString(value)
    ? permissions.includes(value)
    : isIncludeAllChildren(value, permissions);
  return isAuths ? true : false;
};
